import { Link } from 'react-router-dom';
import { Shield, Database, Eye, Lock, UserCheck, Globe, Mail } from 'lucide-react';

export default function Datenschutz() {
  return (
    <div className="section-spacing" data-testid="datenschutz-page">
      <div className="container-wide">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0284C7]">Start</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0F172A]">Datenschutz</span>
        </nav>

        <div className="max-w-3xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-lg bg-[#0284C7]/10 flex items-center justify-center">
              <Shield className="text-[#0284C7]" size={24} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-[var(--font-heading)]">
              Datenschutzerklärung
            </h1>
          </div>

          <div className="bg-[#0284C7]/5 border border-[#0284C7]/20 rounded-xl p-6 mb-8">
            <p className="text-[#0F172A] font-medium">
              Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Diese Datenschutzerklärung 
              informiert Sie gemäß der EU-Datenschutz-Grundverordnung (DSGVO) und dem italienischen 
              Datenschutzgesetz (D.Lgs. 196/2003) über die Verarbeitung personenbezogener Daten.
            </p>
          </div>

          <div className="space-y-8 text-[#475569]">
            
            {/* 1. Verantwortlicher */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  1. Verantwortlicher
                </h2>
              </div>
              <p className="mb-4">
                Verantwortlicher für die Datenverarbeitung auf dieser Website im Sinne der 
                DSGVO ist:
              </p>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="font-medium text-[#0F172A]">HAFNER EC GmbH</p>
                <p>Sinich-Bach-Str. 60</p>
                <p>I-39012 Meran (BZ), Italien</p>
                <p className="mt-2">
                  E-Mail: <a href="mailto:info@hafner-ec.com" className="text-[#0284C7] hover:underline">info@hafner-ec.com</a>
                </p>
                <p>Tel.: +39 0473 238 030</p>
              </div>
            </section>

            {/* 2. Datenschutz auf einen Blick */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  2. Datenschutz auf einen Blick
                </h2>
              </div>
              <p className="mb-4">
                Diese Website ist als <strong>statische Website ohne Datenbank</strong> konzipiert 
                und erhebt nur minimale Daten:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Keine Registrierung erforderlich:</strong> Sie können alle Inhalte 
                  ohne Anmeldung nutzen.
                </li>
                <li>
                  <strong>Keine Cookies für Tracking:</strong> Wir verwenden keine 
                  Tracking-Cookies oder Analyse-Tools wie Google Analytics.
                </li>
                <li>
                  <strong>Lokale Verarbeitung:</strong> Die interaktiven Elemente (Quiz, 
                  Schätzspiele, Entscheidungshilfen) verarbeiten alle Daten ausschließlich 
                  in Ihrem Browser – es werden keine Eingaben an Server übertragen.
                </li>
              </ul>
            </section>

            {/* 3. Server-Log-Dateien */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Database className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  3. Server-Log-Dateien
                </h2>
              </div>
              <p className="mb-4">
                Der Provider dieser Website erhebt und speichert automatisch Informationen 
                in sogenannten Server-Log-Dateien, die Ihr Browser automatisch übermittelt:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Browsertyp und Browserversion</li>
                <li>Verwendetes Betriebssystem</li>
                <li>Referrer URL (zuvor besuchte Seite)</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse (anonymisiert)</li>
              </ul>
              <p className="text-sm">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes 
                Interesse an der Sicherstellung des technischen Betriebs).<br />
                <strong>Speicherdauer:</strong> Diese Daten werden nach spätestens 7 Tagen 
                automatisch gelöscht.
              </p>
            </section>

            {/* 4. Cookies */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  4. Cookies
                </h2>
              </div>
              <p className="mb-4">
                Diese Website verwendet <strong>keine Tracking-Cookies</strong> und keine 
                Analyse-Tools von Drittanbietern.
              </p>
              <p className="mb-4">
                Es werden lediglich technisch notwendige Cookies verwendet, die für den 
                ordnungsgemäßen Betrieb der Website erforderlich sind (z.B. Session-Cookies 
                für die Navigation). Diese Cookies werden beim Schließen des Browsers 
                automatisch gelöscht.
              </p>
              <p className="text-sm">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes 
                Interesse am technischen Betrieb der Website).
              </p>
            </section>

            {/* 5. Externe Links */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  5. Externe Links
                </h2>
              </div>
              <p className="mb-4">
                Diese Website enthält Links zu externen Websites Dritter:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>hafner-ec.com (Bauunternehmen)</li>
                <li>amadeus.immo (Immobilienmakler)</li>
                <li>sanieren.it (Sanierungsportal)</li>
                <li>kohlbrenner.com (Unterkünfte)</li>
              </ul>
              <p>
                Beim Klicken auf diese Links verlassen Sie unsere Website. Für die 
                Datenschutzpraktiken und Inhalte dieser externen Websites übernehmen 
                wir keine Verantwortung. Bitte informieren Sie sich auf den jeweiligen 
                Seiten über deren Datenschutzbestimmungen.
              </p>
            </section>

            {/* 6. Ihre Rechte */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  6. Ihre Rechte nach DSGVO
                </h2>
              </div>
              <p className="mb-4">
                Sie haben jederzeit folgende Rechte bezüglich Ihrer personenbezogenen Daten:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>
                  <strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Sie können Auskunft über 
                  Ihre bei uns gespeicherten Daten verlangen.
                </li>
                <li>
                  <strong>Berichtigungsrecht (Art. 16 DSGVO):</strong> Sie können die 
                  Berichtigung unrichtiger Daten verlangen.
                </li>
                <li>
                  <strong>Löschungsrecht (Art. 17 DSGVO):</strong> Sie können die Löschung 
                  Ihrer Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten 
                  entgegenstehen.
                </li>
                <li>
                  <strong>Einschränkung der Verarbeitung (Art. 18 DSGVO):</strong> Sie 
                  können die Einschränkung der Verarbeitung verlangen.
                </li>
                <li>
                  <strong>Datenübertragbarkeit (Art. 20 DSGVO):</strong> Sie können die 
                  Herausgabe Ihrer Daten in einem gängigen Format verlangen.
                </li>
                <li>
                  <strong>Widerspruchsrecht (Art. 21 DSGVO):</strong> Sie können der 
                  Verarbeitung Ihrer Daten jederzeit widersprechen.
                </li>
              </ul>
              <p className="text-sm">
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:{' '}
                <a href="mailto:info@hafner-ec.com" className="text-[#0284C7] hover:underline">
                  info@hafner-ec.com
                </a>
              </p>
            </section>

            {/* 7. Beschwerderecht */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  7. Beschwerderecht bei der Aufsichtsbehörde
                </h2>
              </div>
              <p className="mb-4">
                Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen 
                Daten gegen die DSGVO verstößt, haben Sie das Recht, sich bei einer 
                Aufsichtsbehörde zu beschweren.
              </p>
              <div className="bg-[#F8FAFC] rounded-lg p-4 text-sm">
                <p className="font-medium text-[#0F172A]">Zuständige Aufsichtsbehörde in Italien:</p>
                <p>Garante per la protezione dei dati personali</p>
                <p>Piazza Venezia, 11 - 00187 Roma</p>
                <p>
                  Website:{' '}
                  <a 
                    href="https://www.garanteprivacy.it" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#0284C7] hover:underline"
                  >
                    www.garanteprivacy.it
                  </a>
                </p>
              </div>
            </section>

            {/* 8. SSL-Verschlüsselung */}
            <section className="bg-white rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-[#0284C7]" size={20} />
                <h2 className="text-xl font-semibold text-[#0F172A] font-[var(--font-heading)]">
                  8. SSL-/TLS-Verschlüsselung
                </h2>
              </div>
              <p>
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung 
                vertraulicher Inhalte eine SSL-/TLS-Verschlüsselung. Eine verschlüsselte 
                Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von 
                „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer 
                Browserzeile.
              </p>
            </section>

            {/* Aktualisierung */}
            <section className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
              <p className="text-sm text-[#64748B]">
                <strong>Stand dieser Datenschutzerklärung:</strong> Januar 2026<br />
                Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, 
                um sie an geänderte rechtliche Anforderungen oder Änderungen unserer 
                Website anzupassen.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
